





































































































































import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  useFetch,
} from '@nuxtjs/composition-api';
import {
  SfReview,
  SfTabs,
  SfLoader,
  SfAccordion
} from '@storefront-ui/vue';
import ProductAddReviewForm from '~/modules/catalog/product/components/ProductAddReviewForm.vue';
import HTMLContent from '~/components/HTMLContent.vue';
import reviewGetters, {
  getReviewId,
  getReviewAuthor,
  getReviewDate,
  getReviewMessage,
  getReviewRating,
} from '~/modules/review/getters/reviewGetters';
import { useReview, UseReviewAddReviewParams } from '~/modules/review/composables/useReview';
import { Product } from '~/modules/catalog/product/types';
import { TabsConfig } from '~/modules/catalog/product/composables/useProductTabs';
import { usePageStore } from '~/stores/page';
import { ComplexTextValue } from '~/composables';
import { parse } from 'node-html-parser';


export default defineComponent({
  name: 'ProductTabs',
  components: {
    ProductAddReviewForm,
    HTMLContent,
    SfReview,
    SfTabs,
    SfLoader,
    SfAccordion
  },
  props: {
    product: {
      type: [Object, null] as PropType<Product | null>,
      default: null,
    },
    openTab: {
      type: Number,
      default: TabsConfig.description.ID,
    },
  },
  setup(props, { emit }) {
    const { routeData } = usePageStore();
    const reviews = ref(null);
    const isReviewsLoading = ref(true);
    const opened = ref<boolean[]>([]);
    const accordionItems = [
      {
        id: 'acc-1',
        summary: 'Where is my order?',
        details:
          'We will inform you about the expected delivery time of your order in checkout and in your order confirmation email.',
      },
      {
        id: 'acc-2',
        summary: 'What if an item is out of stock?',
        details:
          "If an item you're interested in is sold out, you can register to be notified when your size is back in stock.",
      },
      {
        id: 'acc-3',
        summary: 'How do I cancel my order?',
        details:
          "If you made a mistake or simply changed your mind after placing an order, there's no need to fuss. As long as your parcel has yet to be picked and packed in our warehouse, you'll have the option to cancel.",
      },
    ];  
    const {
      search: searchReviews,
      addReview,
    } = useReview();

    const getSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
    });

    const fetchReviews = async (query = getSearchQuery()) => {
      const productReviews = await searchReviews(query);
      const baseReviews = Array.isArray(productReviews)
        ? productReviews[0]
        : productReviews;

      reviews.value = reviewGetters.getItems(baseReviews);
    };

    let lastReviewsQuery = '';
    const newQuery = getSearchQuery();
    const stringNewQuery = JSON.stringify(newQuery);
    if (lastReviewsQuery !== stringNewQuery) {
      lastReviewsQuery = stringNewQuery;
      fetchReviews(newQuery);
      isReviewsLoading.value = false;
    }
    
    const changeTab = (tabNumber: number) => {
      if (!process.client) return props.openTab;
      const tabs = document.querySelector('#tabs');

      if (!tabs) return props.openTab;
      tabs.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });

      if (tabNumber === TabsConfig.reviews.ID) {
        const newQuery = getSearchQuery();
        const stringNewQuery = JSON.stringify(newQuery);
        if (lastReviewsQuery !== stringNewQuery) {
          lastReviewsQuery = stringNewQuery;
          fetchReviews(newQuery);
          isReviewsLoading.value = false;
        }
      }
      emit('changeTab', tabNumber);
      return tabNumber;
    };

    const activeTab = computed(() => changeTab(props.openTab));

    const productDescription = computed(
      () => props.product?.description?.html || '',
    );

    const successAddReview = async (reviewData: UseReviewAddReviewParams) => {
      await addReview(reviewData);
      document.querySelector('#tabs').scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };

    let productSpecifications = '';

    function getSpecifications() {
      if (props.product && props.product.product_specification_details) {
        const parsedContent = parse(String(props.product.product_specification_details));
        const contentContainer = parsedContent.querySelectorAll('div[data-appearance="default"]');
        return contentContainer[0].innerHTML;
      }
    }

    return {
      reviews,
      activeTab,
      changeTab,
      isReviewsLoading,
      getReviewId,
      getReviewAuthor,
      getReviewDate,
      getReviewMessage,
      getReviewRating,
      productDescription,
      successAddReview,
      productSpecifications,
      getSpecifications,
      accordionItems,
      opened
    };
  },
});
